/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The console's colour vocabulary, in one place.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY CLASS HERE IS THE MOCKUP'S, WITH ITS TOKENS RENAMED — `decisions.md` S-17.** The
 * redesign paints with `--ok-bg`, `--ok-fg`, `--warn`, `--warn-bg` and `--warn-fg`; none of the five
 * exists here, and the parity gate refuses a token added locally. So each one maps onto the design
 * system's `--status-*` family, which is the same idea under the names this product already uses:
 *
 *     --ok-bg   → --status-ok-soft        --warn     → --status-warn-line
 *     --ok-fg   → --status-ok-text        --warn-bg  → --status-warn-soft
 *                                         --warn-fg  → --status-warn-text
 *
 * ⚠️ **And `text-white` on the destructive badge becomes `text-destructive-foreground`**, because
 * the lint refuses white: there is no theme of the eight where a hard-coded white is right on every
 * fill, and the design system has an on-colour for each one.
 *
 * ⚠️ **Literal strings, never assembled.** Tailwind finds classes by reading the source; a class
 * built from parts at runtime is a class that is never generated.
 */

/** The pill every status in the console is drawn as: `h-5`, rounded, 11.5px. */
export const PILL =
  'inline-flex h-5 items-center rounded-full px-2 text-[11.5px] font-medium whitespace-nowrap';

export const TONE = {
  ok: 'bg-[var(--status-ok-soft)] text-[var(--status-ok-text)]',
  primary: 'bg-primary/10 text-primary',
  warn: 'bg-[var(--status-warn-soft)] text-[var(--status-warn-text)]',
  muted: 'bg-muted text-muted-foreground',
  bad: 'bg-destructive text-destructive-foreground',
  badSoft: 'bg-destructive/10 text-destructive',
} as const;

export type Tone = keyof typeof TONE;

/** The warning-outlined button the mockup draws for *Suspend*. */
export const WARN_BUTTON =
  'border-[color:var(--status-warn-line)] bg-transparent text-[var(--status-warn-text)] hover:bg-[var(--status-warn-soft)] hover:text-[var(--status-warn-text)]';

/** The destructive-outlined button the mockup draws for *Prepare deletion*. */
export const DANGER_OUTLINE_BUTTON =
  'border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive';

/** A table's header row, verbatim from every list in the mockup. */
export const TH_ROW =
  'text-muted-foreground text-left text-[11px] font-medium tracking-[.03em] uppercase';
export const TH = 'h-8 px-3 font-medium';

/** The section label the mockup puts in small caps: *ÁLLAPOT MOST*, *LÁTOD*, *KAPCSOLATTARTÓ*. */
export const EYEBROW = 'text-[11px] font-medium tracking-[.03em] uppercase';

/**
 * ⚠️ **The mockup's input skin: filled, borderless until hovered.** `bg-muted/50` at rest, the
 * border arrives on hover and the ring on focus — which is also the standing rule for every input
 * in this product, so the two agree rather than one overriding the other.
 */
export const FIELD =
  'bg-muted/50 hover:border-input focus:border-ring focus:ring-ring/30 h-9 rounded-md border border-transparent px-2.5 text-[13px] outline-none focus:ring-[3px]';

/** The mockup's filter-and-select skin: transparent, borderless until hovered, 32px. */
export const SELECT_SKIN =
  'h-8 border-transparent bg-transparent px-2.5 text-[13px] shadow-none hover:border-input';

/**
 * ⚠️⚠️ **THE DESIGN SYSTEM'S BUTTON HEIGHTS, PASSED EXPLICITLY, BECAUSE `ui`'s BUTTON DIFFERS.**
 * `docs/design-system/components/core/Button.jsx` sizes `default` at `h-8` and `sm` at `h-7`; the
 * product's `Button` draws them at `h-9` and `h-8`. The mockup is built on the design system's, so
 * every button in it is 4px shorter than ours. Changing `ui` would move every button in the product
 * on the strength of a console redesign; passing the design system's own numbers here matches the
 * mockup without doing that. The divergence itself is raised, not settled, by this file.
 */
export const BTN = 'h-8 px-3 has-[>svg]:px-2.5';
export const BTN_SM = 'h-7 gap-1.5 px-2.5 text-[13px] has-[>svg]:px-2';
